import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { Lijst } from '@xxtract/tabelfilters';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import type { CandidateRule } from '../dqs.api';
import { StatusBadge, getStatusRowBg } from './StatusBadge';
import { AutoCorrectionToggle } from './AutoCorrectionToggle';
import { RuleDetailDialog } from './RuleDetailDialog';

/** spec-tabelfilters story 4: de kolommen van regelinzicht, eenmalig beschreven (§2). */
export const REGEL_LIJST: Lijst<CandidateRule> = {
  kolommen: [
    { sleutel: 'veld', kopSleutel: 'dqs.detail.fieldName', type: 'tekst', waarde: (r) => r.fieldName },
    { sleutel: 'transformatie', kopSleutel: 'dqs.detail.transformation', type: 'tekst', waarde: (r) => `${r.exampleFromValue} \u2192 ${r.exampleToValue}` },
    { sleutel: 'soort', kopSleutel: 'dqs.detail.type', type: 'keuzelijst', waarde: (r) => r.transformationType, waardeLabel: (w, t) => t(`dqs.transformation.${w}`), vasteWaarden: ['formatting', 'mapping'] },
    { sleutel: 'laag', kopSleutel: 'dqs.detail.layer', type: 'getal', waarde: (r) => r.layer },
    { sleutel: 'zekerheid', kopSleutel: 'dqs.detail.confidence', type: 'getal', waarde: (r) => r.confidence },
    { sleutel: 'bevestigingen', kopSleutel: 'dqs.detail.confirmations', type: 'getal', waarde: (r) => r.confirmationCount },
    { sleutel: 'acceptatie', kopSleutel: 'dqs.detail.acceptanceRate', type: 'getal', waarde: (r) => r.acceptanceRate },
    { sleutel: 'status', kopSleutel: 'dqs.detail.status', type: 'keuzelijst', waarde: (r) => r.status, waardeLabel: (w, t) => t(`dqs.status.${w}`), vasteWaarden: ['candidate', 'active', 'promoted', 'degraded', 'disabled'] },
    { sleutel: 'autocorrectie', kopSleutel: 'dqs.detail.autoCorrection', type: 'overig', waarde: (r) => (r.autoCorrectionEnabled ? 1 : 0) },
  ],
  standaardSortering: () => [{ sleutel: 'veld', richting: 'op' }],
};
const FILTER_TEST_ID = 'regelinzicht';
const STAND_SLEUTEL = 'regels';
const STARTADRES = [{ lijst: REGEL_LIJST, standSleutel: STAND_SLEUTEL }];
const PER_PAGE = 20;

const KOP_STIJL = { color: 'var(--foreground-heading)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600 } as const;

interface RulesTableProps {
  rules: CandidateRule[];
  /** Blijft bestaan na "Filters wissen" in de linkmelding (de paginakop, `tabIndex={-1}`). */
  kopRef: RefObject<HTMLElement | null>;
}

export function RulesTable({ rules, kopRef }: RulesTableProps) {
  const { t } = useTranslation();
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const { start, geweigerd, wisGeweigerd } = useStartadres(STARTADRES);
  const stand = useTabelStand(REGEL_LIJST, STAND_SLEUTEL, { start: start[STAND_SLEUTEL], paginagrootte: PER_PAGE });
  const context = useMemo(() => ({ t: (sleutel: string) => t(sleutel) }), [t]);
  const tabel = useTabelData(rules, REGEL_LIJST, stand, context);
  const standaard = REGEL_LIJST.standaardSortering?.(context);
  const { pagina: currentPage, paginas: totalPages } = tabel;

  return (
    <>
      <FilterRejectedMessage
        baseTestId={FILTER_TEST_ID}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          stand.reset();
          wisGeweigerd();
        }}
      />
      <div className="mb-4 flex flex-col gap-2">
        <Filterbalk context={context} data={tabel} lijst={REGEL_LIJST} stand={stand} />
        <span className="text-[0.8125rem]" data-testid="rules-total-count" style={{ color: 'var(--foreground-muted)' }}>
          {t('dqs.filter.totalCount', { count: tabel.totaal })}
        </span>
      </div>

      {tabel.rijen.length === 0 ? (
        <div
          className="rounded-xl p-8 text-center text-[0.8125rem]"
          style={{ backgroundColor: 'var(--background)', color: 'var(--foreground-muted)' }}
          data-testid="rules-empty-state"
        >
          {t('dqs.noFilteredResults')}
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-xl"
          style={{ backgroundColor: 'var(--background)', boxShadow: 'var(--card-shadow)' }}
          data-testid="rules-table-container"
        >
          <table className="w-full text-left text-[0.8125rem]">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {REGEL_LIJST.kolommen.map((k) => (
                  <th aria-sort={ariaSort(stand.sortering, k.sleutel)} className="px-4 py-3" key={k.sleutel} scope="col" style={KOP_STIJL}>
                    <SorteerbareKop kolom={k} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} standaard={standaard} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tabel.rijen.map((rule) => (
                <tr
                  key={rule._id || rule.patternKey}
                  onClick={() => setSelectedRuleId(rule._id || rule.patternKey)}
                  className="cursor-pointer transition-colors hover:brightness-95"
                  style={{
                    borderBottom: '1px solid var(--border-soft)',
                    backgroundColor: getStatusRowBg(rule.status),
                  }}
                  data-testid={`rule-row-${rule._id || rule.patternKey}`}
                >
                  <td className="px-4 py-3 font-medium">{rule.fieldName}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {rule.exampleFromValue} &rarr; {rule.exampleToValue}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium"
                      style={{
                        backgroundColor: 'var(--background-muted)',
                        color: 'var(--foreground-muted)',
                      }}
                    >
                      {t(rule.transformationType === 'formatting' ? 'dqs.transformation.formatting' : 'dqs.transformation.mapping')}
                    </span>
                  </td>
                  <td className="px-4 py-3">{rule.layer}</td>
                  <td className="px-4 py-3">{(rule.confidence * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3">{rule.confirmationCount}</td>
                  <td className="px-4 py-3">{(rule.acceptanceRate * 100).toFixed(1)}%</td>
                  <td className="px-4 py-3"><StatusBadge status={rule.status} /></td>
                  <td className="px-4 py-3">
                    <AutoCorrectionToggle rule={rule} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="mt-4 flex items-center justify-between"
          data-testid="rules-pagination"
        >
          <span className="text-[0.8125rem]" style={{ color: 'var(--foreground-muted)' }}>
            {t('dqs.pagination', { currentPage, totalPages, count: tabel.totaal })}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => stand.zetPagina(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium disabled:opacity-40"
              style={{ border: '1px solid var(--border)', color: 'var(--foreground)' }}
              data-testid="pagination-prev"
            >
              {t('common.previous')}
            </button>
            <button
              onClick={() => stand.zetPagina(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium disabled:opacity-40"
              style={{ border: '1px solid var(--border)', color: 'var(--foreground)' }}
              data-testid="pagination-next"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <RuleDetailDialog
        ruleId={selectedRuleId}
        onClose={() => setSelectedRuleId(null)}
      />
    </>
  );
}
